import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { InputProptypes, wrap } from 'components/form/InputWrapper'
import { getClipboardData, pasteAsText } from 'utils'
import { position } from 'caret-pos'
import { sliceWithAltKeywordLength } from 'features/creative/creativeUtil'
import { escape, noop } from 'lodash'

const valueToHtml = text =>
  text?.replace(
    /(<키워드:.*?>)/g,
    `<span class='alt_keyword' contenteditable='false'>$1</span>`
  )
const htmlToValue = element => element.innerText.replace(/\n/g, '')
const style = {
  minWidth: 100,
  height: '100%',
  outline: 'none',
}

const notDefined = {}
const allowKeys = [
  'Backspace',
  'Delete',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Control',
  'Alt',
  'Shift',
]
const ContentEditableInput = forwardRef(
  (
    {
      id,
      uniqIndex,
      name,
      value = notDefined,
      maxLength = Number.MAX_SAFE_INTEGER,
      limit,
      disabled,
      onFocus,
      onBlur,
      onChange,
      onCursorChange = noop,
    },
    outerRef
  ) => {
    const innerRef = useRef()
    const valueRef = outerRef || innerRef
    const [isFocused, setFocused] = useState(false)

    const updateContent = useCallback(
      (callback = noop) => {
        const nextValue = sliceWithAltKeywordLength(
          htmlToValue(valueRef.current),
          limit
        )
        if (valueRef.current.value !== nextValue) {
          valueRef.current.value = nextValue
          callback({ isChanged: true })
        } else {
          callback({ isChanged: false })
        }
      },
      [valueRef, limit]
    )

    const getCaretPosition = () => {
      let caretPosition
      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)
        const preCaretRange = range.cloneRange()
        preCaretRange.selectNodeContents(valueRef.current)
        preCaretRange.setEnd(range.endContainer, range.endOffset)
        caretPosition = preCaretRange.toString().length
      }
      return caretPosition
    }

    useEffect(() => {
      if (
        !isFocused &&
        value !== notDefined &&
        value !== valueRef.current.innerHTML
      ) {
        valueRef.current.innerHTML = valueToHtml(
          value?.match(/(<키워드:.*?>)/g) ? value : escape(value)
        )
        updateContent(
          ({ isChanged }) => isChanged && onChange({ target: valueRef.current })
        )
      }
    }, [isFocused, onChange, valueRef, updateContent, value])

    return (
      <div
        contentEditable
        className="tf_area"
        ref={valueRef}
        style={style}
        onKeyUp={event => {
          updateContent(({ isChanged }) => isChanged && onChange(event))
          onCursorChange(getCaretPosition())
        }}
        onMouseUp={() => onCursorChange(getCaretPosition())}
        onKeyDown={event => {
          const selection = document.getSelection()
          const { isCollapsed, anchorNode, focusNode } = selection
          const hasRangeSelection =
            valueRef.current.contains(anchorNode) &&
            valueRef.current.contains(focusNode) &&
            !isCollapsed
          if (
            disabled ||
            event.key === 'Enter' ||
            (valueRef.current.value?.length >= maxLength &&
              !hasRangeSelection &&
              !allowKeys.includes(event.key))
          ) {
            event.preventDefault()
          }
        }}
        onCompositionStart={event => {
          const selection = document.getSelection()
          const { isCollapsed, anchorNode, focusNode } = selection
          const hasRangeSelection =
            valueRef.current.contains(anchorNode) &&
            valueRef.current.contains(focusNode) &&
            !isCollapsed
          updateContent(() => {
            const { innerHTML } = valueRef.current
            if (
              !hasRangeSelection &&
              valueRef.current.value.length >= maxLength
            ) {
              const caretPos = position(valueRef.current).pos
              valueRef.current.blur()
              valueRef.current.contentEditable = false
              valueRef.current.innerHTML = innerHTML
              setTimeout(() => {
                if (valueRef.current) {
                  valueRef.current.contentEditable = true
                  position(
                    valueRef.current,
                    Math.min(caretPos, valueRef.current.innerText.length)
                  )
                }
              }, 200)
            }
            return onChange(event)
          })
        }}
        onPaste={event => {
          event.preventDefault()
          const currentLength = valueRef.current.value
            ? valueRef.current.value.length
            : 0
          if (disabled === false && currentLength < maxLength) {
            const parsedText = getClipboardData(event)
            const text = sliceWithAltKeywordLength(
              parsedText.replace(/\n/g, ''),
              limit
            )
            pasteAsText(text)
            updateContent(({ isChanged }) => isChanged && onChange(event))
          }
        }}
        onBlur={event => {
          setFocused(false)
          onBlur(event)
        }}
        onFocus={event => {
          setFocused(true)
          onFocus(event)
        }}
      />
    )
  }
)

ContentEditableInput.propTypes = {
  ...InputProptypes,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onCursorChange: PropTypes.func,
  limit: PropTypes.number,
}
ContentEditableInput.displayName = 'ContentEditableInput'

export const CreativeContentEditableTextArea = wrap(ContentEditableInput, {
  className: 'box_textarea2 textarea_s',
})
